import PlusIcon from '@heroicons/react/20/solid/PlusIcon'
import { t } from 'i18next'
import { SetStateAction, useContext, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { getCodeDescription } from '../../../api/codedescapi'
import { getSchoolBoards } from '../../../api/schoolboardapi'
import childPlanFormDataContext, {
    IChildPlanFormData,
} from '../../../contexts/ChildPlanFormDataContext'
import ParentFormDataContext from '../../../contexts/ParentFormDataContext'
import returnCustomerFormDataContext from '../../../contexts/ReturnCustomerFormDataContext'
import { ChildFormDataInputObject } from '../../../contexts/contextDataObjects/ChildDataObject'
import i18n from '../../../i18n'
import { IErrorInputMessage } from '../../../model/IErrorInputMessage'
import { handleDDLBlur } from '../../../utils/ddlBlur'
import { handleDDLFocus } from '../../../utils/ddlFocus'
import { fetchPolicyQuote } from '../../../utils/fetchPolicyQuote'
import { setRouteParam } from '../../../utils/routeHelper'
import Button from '../../button/Button'
import DropDownList, { ddlListItem } from '../../dropDownList/DropDownList'
import Modal from '../../modal/Modal'
import RadioGroup, { IRadioOption } from '../../radio/Radio'
import ChildPlans, { fetchPolicyQuoteErrorMessages } from '../childPlans/ChildPlans'
import ChildSection from '../childPlans/ChildSection'
import DoNotOfferErrorMsg from '../doNotOfferErrorMsg/DoNotOfferErrorMsg'
import ParentGuardian from '../parentGuardian/ParentGuardian'
import ProceedToPayment from '../proceedToPayment/ProceedToPayment'
import ReturnCustomer from '../returnCustomer/ReturnCustomer'
import TotalCost from '../totalCost/TotalCost'
export interface IPolicyBooking {
    className?: string
    setShowConfirmation: React.Dispatch<SetStateAction<boolean>>
    setIsModalOpen: React.Dispatch<SetStateAction<boolean>>
    isModalOpen: boolean
}

const PolicyBooking: React.FC<IPolicyBooking> = ({
    className,
    setShowConfirmation,
    setIsModalOpen,
    isModalOpen
}) => {
    const { parentFormData, setParentFormData } = useContext(
        ParentFormDataContext
    )

    const { childPlanFormData, setChildPlanFormData } = useContext(
        childPlanFormDataContext
    )

    const { returnCustomerFormData, setReturnCustomerFormData } = useContext(
        returnCustomerFormDataContext
    )

    const provinceDDL = parentFormData.ParentFormDataInput.find(
        (x) => x.Name === 'Province'
    )

    const [isLoading, setIsLoading] = useState<boolean>(false)

    const languageOptions: IRadioOption[] = [
        {
            label: 'English',
            value: 0,
        },
        {
            label: 'French',
            value: 1,
        },
    ]

    const parentFormRef = useRef<HTMLDivElement>(null)
    const autoEnrollFormRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        getProvinces()
        window.scrollTo(0, 0)
    }, [])

    async function getProvinces() {
        //Set the Return Url for when the policy is Purchased 
        const urlParameters = new URLSearchParams(window.location.search)

        let returnUrl = window.location.protocol +
            '//' +
            window.location.host +
            window.location.pathname

        if (urlParameters.toString().includes("returnTo=1")) {
            returnUrl = "https://insuremykids.com"
            const baseUrl = window.location.protocol +
                '//' +
                window.location.host +
                window.location.pathname +
                "?" +
                urlParameters.toString().replace("&returnTo=1", "")

            window.history.pushState({ path: baseUrl }, '', baseUrl)
        }
        else if (urlParameters.toString().includes("returnTo=2")) {
            returnUrl = "https://manitobastudentinsurance.ca/"
            const baseUrl = window.location.protocol +
                '//' +
                window.location.host +
                window.location.pathname +
                "?" +
                urlParameters.toString().replace("&returnTo=2", "")

            window.history.pushState({ path: baseUrl }, '', baseUrl)
        }
        else {
            returnUrl = returnUrl.replace("/policybooking", "")
        }

        // fetch data from an API when the component mounts
        setIsLoading(true)
        try {
            const provinces = await getCodeDescription('prov', 0)
            const options: ddlListItem[] = provinces
                .filter((x) => x.code != 'N/A')
                .map((province) => ({
                    name: province.description,
                    id: province.code,
                }))
            //Get the URL to return to once Policy has been purchased
            const dataInput = parentFormData.ParentFormDataInput.find(
                (x) => x.Name === 'Province'
            )

            if (dataInput != null && options) {
                const updated = {
                    ...dataInput,
                    ListItems: options,
                }
                const updatedFormInputs = [
                    ...parentFormData.ParentFormDataInput,
                ]
                const index = updatedFormInputs.indexOf(dataInput)
                updatedFormInputs[index] = updated
                setParentFormData((prevState) => (
                    {
                        ...prevState,
                        ParentFormDataInput: updatedFormInputs,
                        ReturnHome: returnUrl
                    }))
            }
        } catch (error) {
            toast.error('Error - Could not load Provinces')
        } finally {
            setIsLoading(false)
        }
    }

    const handleCloseModal = () => {
        setRouteParam(parentFormData.Province.id, parentFormData.Language)
        setIsModalOpen(false)
    }

    const handleDDLChange = async (name: string, value: ddlListItem) => {
        let isValid: boolean = false
        let errorMessage: string = ''

        //Find Province dropdown
        const dataInput = parentFormData.ParentFormDataInput.find(
            (x) => x.Name === name
        )

        if (dataInput != null) {
            // Validation - required
            if (
                dataInput.Required &&
                !value?.name &&
                !parentFormData.DoNotOffer
            ) {
                isValid = false

                const temp = dataInput.ErrorMessageList.find(
                    (x) => x.type === 'required'
                )

                if (temp != null) {
                    errorMessage = temp.error
                }
            } else {
                isValid = true
            }

            const updatedTemp = {
                ...dataInput,
                IsBlur: true,
                IsFocus: false,
                IsValid: isValid,
                ErrorMessage: errorMessage,
            }

            try {
                const schoolBoards = await getSchoolBoards(value.id)
                if (schoolBoards.length > 0) {
                    setParentFormData((prevState) => ({
                        ...prevState,
                        DoNotOffer: false
                    }))
                }
            }
            catch (error: any) {
                setParentFormData((prevState) => ({
                    ...prevState,
                    DoNotOffer: true
                }))
            }
            const updatedFormInputs = [...parentFormData.ParentFormDataInput]
            const index = updatedFormInputs.indexOf(dataInput)
            updatedFormInputs[index] = updatedTemp
            setParentFormData((prevState) => ({
                ...prevState,
                [name]: value,
                ParentFormDataInput: updatedFormInputs,
            }))
        }

    }

    const handleSelectLanguage = (option: number) => {
        setParentFormData({ ...parentFormData, ['Language']: option })
        const language = option == 0 ? 'enUS' : 'frCA'
        i18n.changeLanguage(language)
    }

    const handleModalBtnDisabled = (): boolean => {
        if (parentFormData?.Province?.id && parentFormData?.Language) {
            return false
        }
        return true
    }

    const addChild = (): IChildPlanFormData[] => {
        let updatedChildArr = [...childPlanFormData]
        let newChild = {
            FirstName: '',
            LastName: '',
            Dob: '',
            School: { id: '', name: '' },
            Plan: {
                PlanCode: '',
                Primary: '',
                Premium: 0,
                Index: -1,
                IsValid: false
            },
            ChildPlanFormDataInput: ChildFormDataInputObject,
            SetValidationFocus: false
        }

        newChild.Plan = childPlanFormData[childPlanFormData.length - 1].Plan
        let newChildInput = newChild.ChildPlanFormDataInput.find(x => x.Name === 'School')
        if (parentFormData.OrgGuid || parentFormData.RegionId === 400002158) {
            newChild.School = childPlanFormData[childPlanFormData.length - 1].School

            if (newChildInput) {
                newChildInput.IsBlur = true
                newChildInput.IsFocus = false
                newChildInput.IsValid = true
                newChildInput.Disabled = newChild.School.id ? true : false
            }
        }
        if (newChildInput) {
            newChildInput.ListItems = parentFormData.SchoolListItems
        }

        updatedChildArr.push(newChild)
        setChildPlanFormData(updatedChildArr)
        return updatedChildArr
    }

    const handleAddChildBtn = () => {
        const updatedChildData = addChild()
        fetchPolicyQuote(
            parentFormData,
            updatedChildData,
            setParentFormData,
            setChildPlanFormData
        )
    }

    const handleConfirmBtn = () => {
        let invalidParentFormInputs: IErrorInputMessage[] = []
        let invalidSchoolBoard: IErrorInputMessage[] = []
        let invalidChildPlanFormInputs: IErrorInputMessage[] = []
        let validChildPlanSelection: boolean = true
        const updateParentFormData = { ...parentFormData }
        updateParentFormData.ParentFormDataInput =
            updateParentFormData.ParentFormDataInput.map((input) => {
                if (
                    (input.Name &&
                        input.ControlType === 'DropDownList' &&
                        updateParentFormData[input.Name].id === '') ||
                    (input.ControlType !== 'DropDownList' &&
                        updateParentFormData[input.Name] === '')
                ) {
                    return {
                        ...input,
                        IsValid: false,
                        IsBlur: true,
                        ErrorMessage: 'required',
                    }
                }
                return input
            })
        updateParentFormData.AutoEnroll.IsValid = true

        if (updateParentFormData.SchoolBoard.id === '') {
            updateParentFormData.CommonChildPlanFormInputData.IsValid = false
            updateParentFormData.CommonChildPlanFormInputData.IsBlur = true
            updateParentFormData.CommonChildPlanFormInputData.ErrorMessage =
                'required'
        }
        if (!updateParentFormData.StartDate) {
            updateParentFormData.CommonChildPlanStartDate.IsValid = false
            updateParentFormData.CommonChildPlanStartDate.IsBlur = true
            updateParentFormData.CommonChildPlanStartDate.ErrorMessage =
                'required'
        }
        setParentFormData(updateParentFormData)
        parentFormData.ParentFormDataInput.forEach((input) => {
            if (!input.IsValid) {
                invalidParentFormInputs.push({
                    type: input.Label,
                    error: input.ErrorMessage,
                })
            }
        })

        if (!parentFormData.CommonChildPlanFormInputData.IsValid) {
            invalidSchoolBoard.push({
                type: parentFormData.CommonChildPlanFormInputData.Label,
                error: parentFormData.CommonChildPlanFormInputData.ErrorMessage,
            })
        }
        const autoEnrollValid =
            parentFormData.PlanDuration.Duration === 1
                ? parentFormData.AutoEnroll.Renew !== ''
                : true

        let newChildPlanFormData = [...childPlanFormData]
        const updatedFormData = newChildPlanFormData.map((childPlan, index) => {
            const updatedChildPlanData = childPlan.ChildPlanFormDataInput.map(
                (input) => {
                    if (
                        (input.Name &&
                            input.ControlType === 'DropDownList' &&
                            childPlan[input.Name].id === '') ||
                        (input.ControlType !== 'DropDownList' &&
                            childPlan[input.Name] === '')
                    ) {
                        return {
                            ...input,
                            IsValid: false,
                            IsBlur: true,
                            ErrorMessage: 'required',
                        }
                    }
                    return input
                }
            )

            if (childPlan.Plan.PlanCode == "" || (!childPlan.Plan.Premium && !parentFormData.FamilyRate)) {
                validChildPlanSelection = false
            }

            let setValidationFocus = false
            if (
                invalidParentFormInputs.length === 0 &&
                invalidSchoolBoard.length === 0 &&
                autoEnrollValid &&
                !updatedChildPlanData.every((x) => x.IsValid)
            ) {
                if (index === 0) {
                    setValidationFocus = true
                } else {
                    setValidationFocus = newChildPlanFormData[
                        index - 1
                    ].ChildPlanFormDataInput.every((x) => x.IsValid)
                }
            }

            return {
                ...childPlan,
                ChildPlanFormDataInput: updatedChildPlanData,
                SetValidationFocus: setValidationFocus,
                Plan: {
                    Index: childPlan.Plan.Index,
                    PlanCode: childPlan.Plan.PlanCode,
                    Primary: childPlan.Plan.Primary,
                    Premium: childPlan.Plan.Premium,
                    IsValid: true
                }
            }
        })

        setChildPlanFormData(updatedFormData)

        childPlanFormData.forEach((childPlan) => {
            childPlan.ChildPlanFormDataInput.forEach((input) => {
                if (!input.IsValid) {
                    invalidChildPlanFormInputs.push({
                        type: input.Label,
                        error: input.ErrorMessage,
                    })
                }
            })
        })

        if (
            parentFormData.CommonChildPlanStartDate.IsValid &&
            invalidParentFormInputs.length === 0 &&
            invalidSchoolBoard.length === 0 &&
            invalidChildPlanFormInputs.length === 0 &&
            validChildPlanSelection &&
            autoEnrollValid
        ) {
            setShowConfirmation(true)
        } else {
            if (invalidParentFormInputs.length > 0) {
                parentFormRef.current?.scrollIntoView({ behavior: 'smooth' })
            } else if (invalidSchoolBoard.length > 0) {
                autoEnrollFormRef.current?.scrollIntoView({
                    behavior: 'smooth',
                })
            } else if (!autoEnrollValid) {
                document.getElementById('autoEnroll')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            } else if (!parentFormData.CommonChildPlanStartDate.IsValid) {
                autoEnrollFormRef.current?.scrollIntoView({
                    behavior: 'smooth',
                })
            }
            else if (!validChildPlanSelection) {
                document.getElementById('Plan')?.scrollIntoView({
                    behavior: 'smooth', block: 'center'
                })
            }
            toast.warn(
                <>
                    <div>
                        <p>{t('validationWarningText')}</p>
                    </div>
                </>,
                {
                    position: toast.POSITION.TOP_RIGHT,
                }
            )
        }
        fetchPolicyQuoteErrorMessages(
            parentFormData,
            childPlanFormData,
            setParentFormData,
        )
    }

    return (
        <>
            <div className='space-y-[2rem] '>
                {returnCustomerFormData.ShowReturningCustomer ?
                    <div className='bg-[#f5f5f5] h-[82.7vh] '>
                        <ReturnCustomer />
                    </div>
                    : (
                        <>
                            <div className='space-y-7'>
                                <ParentGuardian forwardedRef={parentFormRef} />
                                <ChildPlans forwardedAutoEnrollRef={autoEnrollFormRef} />
                                <ChildSection />
                                <div className='flex justify-center lg:pb-0 pb-2'>
                                    <Button
                                        color="bg-[#9ccb45]"
                                        text="addAdditionalChild"
                                        textSize='text-2xl text-black'
                                        handleClick={handleAddChildBtn}
                                        className="bg-[#9ccb45] hover:bg-[#b2d472] lg:w-[55rem] md:w-[31rem] w-[20rem] shadow-md h-20 text-center font-semibold text-black"
                                        prefix={<PlusIcon className="h-10 w-10" />}
                                    />
                                </div>
                            </div>
                            <TotalCost className='fixed z-40' isModalOpen={isModalOpen} setShowConfirmation={setShowConfirmation} />
                            <div className='lg:flex lg:justify-center'>
                                <ProceedToPayment className='lg:relative fixed z-40' isModalOpen={isModalOpen} handleClick={() => handleConfirmBtn()} />
                            </div>

                        </>
                    )}
            </div>

            <Modal
                title=""
                isOpen={isModalOpen}
                showSubmitBtn={!parentFormData.DoNotOffer}
                handleSave={handleCloseModal}
                disabled={handleModalBtnDisabled()}
                width={'lg:w-[45rem] md:w-[27rem] w-[19rem]'}
            >
                <div className="mt-2 flex flex-col items-center">
                    <DropDownList
                        className="pt-1 z-10 h-36 text-left"
                        width="lg:w-[25rem] md:w-[20rem] w-[16rem]"
                        list={provinceDDL?.ListItems || []}
                        label={t(provinceDDL?.Label || '') || ''}
                        inputID={provinceDDL?.Id + 'modal' || ''}
                        name={provinceDDL?.Name || ''}
                        required={provinceDDL?.Required || true}
                        onChange={handleDDLChange}
                        onBlur={(e) => handleDDLBlur(parentFormData, setParentFormData, e)}
                        onFocus={(e) => handleDDLFocus(parentFormData, setParentFormData, e)}
                        selectedItem={parentFormData.Province}
                        error={t(provinceDDL?.ErrorMessage || '') || ''}
                        isValid={provinceDDL?.IsValid || false}
                        isBlur={provinceDDL?.IsBlur || false}
                        isFocus={provinceDDL?.IsFocus || false}
                        disabled={provinceDDL?.Disabled || false}
                    />

                    {parentFormData.DoNotOffer ? (
                        <DoNotOfferErrorMsg
                            className="lg:w-[24rem] md:w-[19rem] w-[15rem] md:text-base text-sm"
                            province={parentFormData?.Province?.name}
                        />
                    ) : (
                        <RadioGroup
                            className="pt-2 items-center z-5"
                            width="w-[26rem]"
                            label={t('chooseLanguage') || ''}
                            options={languageOptions}
                            handleSelectOption={handleSelectLanguage}
                        />
                    )}
                </div>
            </Modal>
        </>
    )
}

export default PolicyBooking
